import {
  mdiBank,
  mdiCurrencyRupee,
  mdiEmailFast,
  mdiLocationEnter,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useLayoutEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPost } from "../../../service/api_client";
import { ACTION, file_dir, MODULE } from "../../../utils/enums";
import { updateObject } from "../../../utils/formDataToJson";
import { urls } from "../../../utils/urls";
import {
  CountryType,
  LegalEntityType,
  PartyModel,
  PartySegment,
  PartyStatusMap,
  PartyType,
  defaulPartyValue,
  gstTypes,
  taxRegType,
} from "../models/party_model";
import {
  AttachmentModel,
  getFileType,
} from "../../purchase_order/model/purchase_order_model";
import { dataTypes } from "../../../utils/import_export_parser";
import SingleAttachmentSection from "./components/single_attachmement";
import MultiAttachmentSection from "./components/multi_attachment";
import {
  getAttachmentSignedURLAPI,
  isGstUniqueInSystem,
  uploadFiletoS3API,
  VendorCreateRepo,
  VendorSaveDraftRepo,
} from "./repo/parties_repo";
import { VendorAttachmentsTypes } from "./enums/vendor_enums";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { generate_id } from "../../inventory/journals/destination_stock_voucher/components/item_row_status";
import { Country, State, City } from "country-state-city";
import moment from "moment";
import ContactNumberFieldWithCodes from "./components/contact_field_with_codes";
import "../../../styles/responsive_shrink.css";

interface VendorCreateScreenProps {
  onClose: (data?: PartyModel) => void;
  data?: PartyModel;
}

const VendorCreateScreen: FunctionComponent<VendorCreateScreenProps> = (
  props,
) => {
  const { id } = useParams<{ id: string }>();
  const isCreatePage = useRouteMatch({
    path: urls.procurement_masters_party + "/create",
    exact: true,
  });
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/accounts/party`;
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [vendor, setVendor] = useState<PartyModel>({
    ...(props.data || defaulPartyValue),
  });

  const navigation = useHistory();

  const { user, isAuthorised } = useAuth();
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [isMaker, setIsMaker] = useState<boolean>(false);
  const [creating, setCreating] = useState(false);
  const [attachmentKey, setAttachmentKey] = useState(Math.random() * 10000);
  const [newGstNo, setNewGstNo] = useState<string>("");

  const [countries, setCountries] = useState<CountryType[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const [isGstVerified, setIsGstVerified] = useState<boolean>(false);
  const [gstVerificationData, setGstVerificationData] = useState<any>(null);
  const [gstLockedFields, setGstLockedFields] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({
    panError: "",
    pincodeError: "",
    mobileError: "",
    gstError: "",
  });

  const validatePincode = (pincode: string) => {
    const regex = /^[1-9][0-9]{5}$/;
    if (!pincode) return "Pincode is required";
    if (!regex.test(pincode)) {
      return "Invalid pincode (must be 6 digits)";
    }
    return "";
  };

  const validatePAN = (pan: string, isRequired: boolean = false) => {
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (isRequired && !pan) return "PAN is required";
    if (pan && !regex.test(pan.toUpperCase())) {
      return "Invalid PAN format (e.g., ABCDE1234F)";
    }
    return "";
  };

  const validateGSTNumber = (gst: string): string => {
    if (!gst) return "GST number is required.";
    if (gst.length !== 15)
      return "GST number must be exactly 15 characters. Example: 27ABCDE1234F1Z5";
    if (
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst)
    ) {
      return "Invalid GST number format. Example: 27ABCDE1234F1Z5";
    }
    return "";
  };

  const validateContactNumbers = (vendor: any) => {
    const errors: string[] = [];

    // Mandatory contact no check
    if (
      vendor.mail_detail.contact_mobile &&
      !vendor.mail_detail.contact_mobile_country_code
    ) {
      errors.push("Contact No requires a country code");
    }
    if (!vendor.mail_detail.contact_mobile) {
      errors.push("Contact No is mandatory");
    }

    // Optional fields → only check if mobile entered
    if (
      vendor.mail_detail.contact_mobile_accounts_spoc &&
      !vendor.mail_detail.contact_mobile_accounts_spoc_country_code
    ) {
      errors.push("Accounts SPOC Mobile No. requires a country code");
    }

    if (
      vendor.mail_detail.contact_mobile_contact_person &&
      !vendor.mail_detail.contact_mobile_contact_person_country_code
    ) {
      errors.push("Contact Person Mobile No. requires a country code");
    }

    if (
      vendor.mail_detail.contact_detail_ceo_director &&
      !vendor.mail_detail.contact_detail_ceo_director_country_code
    ) {
      errors.push("CEO/Director Contact requires a country code");
    }

    return errors;
  };

  const extractPanFromGstin = (gstin: string) => {
    if (!gstin || gstin.length !== 15) {
      return null;
    }

    // PAN is from index 2 to 12 (10 characters)
    const pan = gstin.substring(2, 12);

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan) ? pan : null;
  };

  const handleChange = (e: any) => {
    const { value, name } = e.target;
    const type = e.target.getAttribute("data-type");
    const keys = name.split(".");
    const updatedValue = updateObject(vendor, keys, value, type);
    setVendor(updatedValue);

    if (name === "tax_detail.pan") {
      const isIndiaSelected = vendor.mail_detail.address.country === "India";
      const error = validatePAN(value, isIndiaSelected);
      setErrors((prev) => ({ ...prev, panError: error }));
    }

    if (name === "mail_detail.address.pincode") {
      const error = validatePincode(value);
      setErrors((prev) => ({ ...prev, pincodeError: error }));
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = event.target.value;

    if (selectedCountry) {
      const countryObj = countries.find((c) => c.name === selectedCountry);

      if (countryObj) {
        const countryStates = State.getStatesOfCountry(countryObj.isoCode);

        if (countryStates.length > 0) {
          setStates(countryStates);
          setCities([]);
        } else {
          setStates([]);
          setCities([]);
        }
      }
    } else {
      setStates([]);
      setCities([]);
    }

    setVendor((prev: any) => ({
      ...prev,
      mail_detail: {
        ...prev.mail_detail,
        address: {
          ...prev.mail_detail.address,
          country: selectedCountry,
          state: "",
          city: "",
        },
      },
    }));
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedState = event.target.value;

    if (selectedState) {
      const stateObj = states.find((s) => s.name === selectedState);
      if (stateObj) {
        setCities(
          City.getCitiesOfState(stateObj.countryCode, stateObj.isoCode),
        );
      }
    } else {
      setCities([]);
    }

    setVendor((prev: any) => ({
      ...prev,
      mail_detail: {
        ...prev.mail_detail,
        address: {
          ...prev.mail_detail.address,
          state: selectedState,
          city: "",
        },
      },
    }));
  };

  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCity = event.target.value;

    setVendor((prev: any) => ({
      ...prev,
      mail_detail: {
        ...prev.mail_detail,
        address: {
          ...prev.mail_detail.address,
          city: selectedCity,
        },
      },
    }));
  };

  const getData = async (id: string) => {
    setLoading(true);
    const res = await fetchGet(url + "/" + id);
    if (res.success) {
      if (!Array.isArray(res.data.tax_detail.gst_no)) {
        res.data.tax_detail.gst_no = [res.data.tax_detail.gst_no];
      }
      setVendor(res.data);
      setIsGstVerified(res.data.is_GST_validated);
      setGstLockedFields([
        "legal_name",
        "tax_detail.gst_no",
        "tax_detail.reg_type",
        "tax_detail.pan",
        "mail_detail.address.line1",
        "mail_detail.address.state",
        "mail_detail.address.country",
        "mail_detail.address.city",
        "mail_detail.address.pincode",
      ]);
      if (res.data.status === PartyStatusMap.Approved) {
        setCanEdit(false);
      }
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const verifyGstNumber = async () => {
    if (!newGstNo || newGstNo.trim() === "") {
      showToast({ type: ToastType.error, text: "Please enter a GST number" });
      return;
    }

    setLoading(true);
    const { unique, error } = await isGstUniqueInSystem(newGstNo);

    if (!unique) {
      showToast({
        type: ToastType.error,
        text:
          error === "Provided GST Number already exists"
            ? "GST number already exists in the system"
            : error || "Something went wrong",
      });
      setLoading(false);
      return;
    }

    try {
      let url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/accounts/party/gst/verify`;
      const response = await fetchPost(url, { gstin: newGstNo });

      if (response.success) {
        // console.log("GST Details:", response.data);
        if (response.data.message === "GSTIN Doesn't Exist") {
          setIsGstVerified(false);
          setGstVerificationData(null);

          showToast({
            type: ToastType.error,
            text: "GSTIN Doesn't Exist",
          });
          return;
        }
        if (response.data.message === "GSTIN Exists") {
          let tempData = response.data;
          let validPan = extractPanFromGstin(tempData.GSTIN);

          // Store verification data
          setGstVerificationData(tempData);
          setIsGstVerified(true);

          setVendor((prev) => {
            let updatedVendor = { ...prev };
            if (!Array.isArray(updatedVendor.tax_detail.gst_no)) {
              updatedVendor.tax_detail.gst_no = [];
            }
            if (!updatedVendor.tax_detail.gst_no.includes(tempData.GSTIN)) {
              updatedVendor.tax_detail.gst_no.push(tempData.GSTIN);
            }

            // Fill verified data
            updatedVendor.legal_name = tempData.legal_name_of_business;
            updatedVendor.name = tempData.trade_name_of_business;
            updatedVendor.tax_detail.reg_type =
              tempData.taxpayer_type === "Composition"
                ? taxRegType.composition
                : taxRegType.regular;

            const address: PartyModel["mail_detail"]["address"] = {
              ...updatedVendor.mail_detail.address,
              type: updatedVendor.mail_detail.address.type ?? "",
            };
            const verifiedAddress = tempData.principal_place_split_address;
            address.line1 =
              verifiedAddress.flat_number +
              ", " +
              verifiedAddress.building_name +
              ", " +
              verifiedAddress.building_number;
            address.line2 = verifiedAddress.street;
            address.line3 = verifiedAddress.location;
            address.state = verifiedAddress.state;
            address.city = verifiedAddress.city;
            address.country = "India";
            address.id = tempData.reference_id.toString();
            address.pincode = verifiedAddress.pincode ?? 0;
            address.type = "Office";

            updatedVendor.mail_detail.address = address;
            updatedVendor.tax_detail.pan = validPan || "";

            const indiaObj = countries.find((c) => c.name === "India");
            if (indiaObj) {
              const stateList = State.getStatesOfCountry(indiaObj.isoCode);
              setStates(stateList);

              const selectedStateObj = stateList.find(
                (s) =>
                  s.name.toLowerCase() === (address.state || "").toLowerCase(),
              );

              if (selectedStateObj) {
                const cityList = City.getCitiesOfState(
                  selectedStateObj.countryCode,
                  selectedStateObj.isoCode,
                );
                setCities(cityList);
              }
            }

            setGstLockedFields([
              "legal_name",
              "tax_detail.reg_type",
              "tax_detail.pan",
              "mail_detail.address.line1",
              "mail_detail.address.state",
              "mail_detail.address.country",
              "mail_detail.address.city",
              "mail_detail.address.pincode",
            ]);

            return updatedVendor;
          });

          showToast({
            type: ToastType.success,
            text: "GST verified successfully! Details have been auto-filled.",
          });
        }
      } else {
        console.error("Error fetching GST details:", response.message);
        showToast({
          type: ToastType.error,
          text: response.message || "Failed to fetch GST details",
        });
      }
    } catch (error) {
      console.error("Error fetching GST details:", error);
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching GST details",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFieldDisabledByGST = (fieldName: string): boolean => {
    if (!isGstVerified) return false;

    // If this field is not controlled by GST lock -> never disable
    if (!gstLockedFields.includes(fieldName)) {
      return false;
    }

    // Find the current value
    const value = (() => {
      switch (fieldName) {
        case "legal_name":
          return vendor?.legal_name;
        case "tax_detail.reg_type":
          return vendor?.tax_detail?.reg_type;
        case "tax_detail.pan":
          return vendor?.tax_detail?.pan;
        case "mail_detail.address.line1":
          return vendor?.mail_detail?.address?.line1;
        case "mail_detail.address.state":
          return vendor?.mail_detail?.address?.state;
        case "mail_detail.address.city":
          return vendor?.mail_detail?.address?.city;
        case "mail_detail.address.pincode":
          return vendor?.mail_detail?.address?.pincode;
        case "mail_detail.address.country":
          return vendor?.mail_detail?.address?.country;
        case "tax_detail.gst_no":
          return vendor?.tax_detail?.gst_no[0];
        default:
          return null;
      }
    })();

    // If GST did not provide value (null/empty string) -> always editable
    if (!value || value.trim?.() === "") {
      return false;
    }
    // If GST provided a value -> lock permanently
    return true;
  };

  const uploadFile = async ({
    file,
    category,
  }: {
    file: File;
    category: string;
  }) => {
    try {
      if (!file || !category) {
        showToast({
          type: ToastType.warning,
          text: "Please select file and category",
        });
        return;
      }
      setLoading(true);
      const { name, type } = file;
      // const create_url = `${url}/${vendor.id}/attachment`;

      const query = {
        mimetype: type,
        extension: name.split(".").pop(),
        dir: file_dir.party_attachments,
      };
      const res = await getAttachmentSignedURLAPI(query);
      if (res.success) {
        const { signed_url, url } = res.data;
        // const aws_res = await axios.put(signed_url, file, {
        //   headers: {
        //     "Content-Type": getFileType(type),
        //   },
        // });

        const aws_res = await uploadFiletoS3API({
          signed_url,
          file,
          type: getFileType(type),
        });

        const attachment: AttachmentModel = {
          id: generate_id(),
          category,
          type: getFileType(type),
          url,
        };
        showToast({
          type: ToastType.success,
          text: "Upload succesfull",
        });

        if (category === VendorAttachmentsTypes.msme) {
          setVendor((o) => {
            const updatedAttachments = (o.attachments || []).map((att) =>
              att.category === VendorAttachmentsTypes.msme ? attachment : att,
            );

            // If no existing msme attachment, add the new one
            const hasMsme = updatedAttachments.some(
              (att) => att.category === VendorAttachmentsTypes.msme,
            );
            if (!hasMsme) {
              updatedAttachments.push(attachment);
            }

            return {
              ...o,
              attachments: updatedAttachments,
            };
          });
        } else {
          setVendor((o) => ({
            ...o,
            attachments: [...(o.attachments || []), attachment],
          }));
        }

        setAttachmentKey(Math.random() * 1000);
        setLoading(false);
        return attachment;
        //Update the current object
        // const create_res = await fetchPost(create_url, attachment);

        // if (create_res.success) {
        // }
      }
      setLoading(false);
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message,
      });
      setLoading(false);
    }
  };

  const handleSaveDraft = async (e: any) => {
    e.preventDefault();
    setCreating(true);

    if (!vendor.name || vendor.name === undefined || vendor.name === "") {
      showToast({ type: ToastType.error, text: "Please fill in Vendor Name" });
      setCreating(false);
      return;
    }
    vendor.is_GST_validated = isGstVerified;
    try {
      const res = await VendorSaveDraftRepo(vendor);
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        if (!vendor.id) {
          setVendor(defaulPartyValue);
        }
        setVendor((prevData) => ({
          ...prevData,
          ...res.data, // Update with the response data
        }));
        setCreating(false);
        navigation.goBack();
        if (props.onClose) props.onClose(res.data);
      } else showToast({ type: ToastType.error, text: res.error });
    } catch (error) {
      console.log(error);
    }
    setCreating(false);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;

    const contactErrors = validateContactNumbers(vendor);
    if (contactErrors.length > 0) {
      showToast({ type: ToastType.error, text: contactErrors[0] });
      return;
    }

    const isIndiaSelected = vendor.mail_detail.address.country === "India";

    if (isIndiaSelected && vendor.tax_detail.pan) {
      if (
        vendor.tax_detail.pan &&
        (!vendor.tax_detail.gstType || vendor.tax_detail.gstType.trim() === "")
      ) {
        showToast({
          type: ToastType.error,
          text: "GST Status (Operative/Inoperative) is mandatory when PAN is provided",
        });
        return;
      }
    }

    if (!form.checkValidity()) {
      const firstInvalidField = form.querySelector(":invalid") as HTMLElement;
      firstInvalidField?.focus();
      form.reportValidity();
      return;
    }

    setCreating(true);
    try {
      if (
        vendor.tax_detail?.reg_type !== taxRegType.unregistered &&
        (!vendor.tax_detail.gst_no || vendor.tax_detail.gst_no.length === 0)
      ) {
        showToast({
          type: ToastType.error,
          text: "GST Number is required for registered vendors",
        });
        setCreating(false);
        return;
      }
      vendor.is_GST_validated = isGstVerified;
      const res = await VendorCreateRepo(vendor);
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        if (!vendor.id) {
          setVendor(defaulPartyValue);
        }
        setVendor((prevData) => ({
          ...prevData,
          ...res.data.value,
        }));
        if (props.onClose) props.onClose(res.data);
        navigation.goBack();
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      console.log(error);
    }
    setCreating(false);
  };

  useEffect(() => {
    const isIndiaSelected = vendor.mail_detail.address.country === "India";

    // Revalidate PAN when country changes
    if (vendor.tax_detail.pan) {
      const panError = validatePAN(vendor.tax_detail.pan, isIndiaSelected);
      setErrors((prev) => ({ ...prev, panError }));
    }
  }, [vendor.mail_detail.address.country]);

  useEffect(() => {
    let is_maker: Boolean = isAuthorised({
      module: MODULE.master_vendor_maker,
      action: ACTION.CREATE,
    });
    if (is_maker) {
      setIsMaker(true);
      setCanEdit(true);
    } else {
      setIsMaker(false);
      setCanEdit(false);
    }
  }, [user]);

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    if (id) getData(id);
  }, []);

  useLayoutEffect(() => {
    if (isCreatePage) {
      setVendor((prev) => ({
        ...prev,
        id: "",
      }));
    }
  }, []);

  return (
    <div className="mobile-shrink">
      <section
        id="top_section"
        className="flex justify-between items-end my-1 "
      >
        <h1 className="m-2 md:m-4 border-myPrimaryColor pl-2 font-bold text-2xl text-black border-l-4 flex items-center gap-3">
          Vendor Create
        </h1>
      </section>

      <form
        id="party_create_form"
        onSubmit={handleSubmit}
        className="grid gap-2 p-2 md:p-5"
      >
        <fieldset disabled={!canEdit}>
          <section className="flex flex-col md:flex-row gap-4">
            <div className="bg-white rounded-md p-2 w-full md:w-1/2">
              <h1 className="mb-2 font-semibold flex gap-2 items-center">
                <span className="text-green-500">
                  <Icon path={mdiCurrencyRupee} size={0.7} />
                </span>
                GST Details
              </h1>
              <div className="gap-3 ">
                <div className="grid gap-0.5 mt-2">
                  <label htmlFor="tax_detail.reg_type" className="text-sm">
                    GST Registration Type
                  </label>
                  <select
                    id="tax_detail.reg_type"
                    name="tax_detail.reg_type"
                    className="border rounded-md px-3 py-1"
                    data-type={dataTypes.string}
                    value={vendor.tax_detail.reg_type}
                    onChange={handleChange}
                    disabled={isFieldDisabledByGST("tax_detail.reg_type")}
                  >
                    <option value=" ">Select</option>
                    {Object.values(taxRegType).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="gap-1">
                <label htmlFor="tax_detail.gst_no" className="text-sm">
                  GST No.{" "}
                  {vendor.tax_detail?.reg_type !== taxRegType.unregistered && (
                    <RequiredMark />
                  )}
                </label>
                <div>
                  <div className="flex justify-between gap-2">
                    <input
                      required={
                        vendor.tax_detail?.reg_type !== taxRegType.unregistered
                      }
                      maxLength={15}
                      type="text"
                      data-type={dataTypes.string}
                      id="tax_detail.gst_no"
                      name="tax_detail.gst_no"
                      className="border rounded-md px-3 py-1 flex-grow"
                      value={newGstNo || vendor.tax_detail.gst_no || ""}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();

                        setNewGstNo(val);
                        setVendor((prev) => ({
                          ...prev,
                          tax_detail: {
                            ...prev.tax_detail,
                            gst_no: [e.target.value],
                          },
                        }));

                        const errorMsg = validateGSTNumber(val);
                        setErrors((prev) => ({
                          ...prev,
                          gstError: errorMsg,
                        }));

                        if (isGstVerified) {
                          setIsGstVerified(false);
                          setGstLockedFields([]);
                          setVendor((prev) => ({
                            ...prev,
                            name: "",
                            legal_name: "",

                            tax_detail: {
                              ...prev.tax_detail,
                              gst_no: [],
                              pan: "",
                              reg_type: "",
                            },
                            mail_detail: {
                              ...prev.mail_detail,
                              address: {
                                ...prev.mail_detail.address,
                                line1: "",
                                line2: "",
                                line3: "",
                                country: "",
                                state: "",
                                city: "",
                                pincode: "",
                                type: "",
                                id: "",
                              },
                            },
                          }));
                        }
                      }}
                      disabled={
                        vendor.tax_detail?.reg_type ===
                          taxRegType.unregistered ||
                        isFieldDisabledByGST("tax_detail.gst_no")
                      }
                    />
                    {vendor.tax_detail?.reg_type !==
                      taxRegType.unregistered && (
                      <>
                        <button
                          id="verify_gst_button"
                          type="button"
                          onClick={verifyGstNumber}
                          disabled={!!validateGSTNumber(newGstNo || "")} // disabled if validation fails
                          className={`px-3 py-1 rounded-md text-white ${
                            validateGSTNumber(newGstNo || "")
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-blue-500 hover:bg-blue-600"
                          }`}
                        >
                          Search
                        </button>
                      </>
                    )}
                  </div>
                  {errors.gstError && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.gstError}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <div className="mt-2">
                  <label htmlFor="tax_detail.gst_no" className="text-sm">
                    Please Attach GST certificates{" "}
                    {vendor.tax_detail?.reg_type !==
                      taxRegType.unregistered && <RequiredMark />}
                  </label>
                </div>
                <MultiAttachmentSection
                  id="gst_attachments"
                  attachments={(vendor.attachments ?? []).filter(
                    (attachment) =>
                      attachment.category === VendorAttachmentsTypes.gst,
                  )}
                  onSubmit={async function (data: {
                    file: File;
                    category: string;
                  }): Promise<AttachmentModel> {
                    const attachment = await uploadFile({
                      file: data.file,
                      category: data.category,
                    });
                    if (!attachment) {
                      throw new Error("File upload failed");
                    }
                    return attachment;
                  }}
                  category={VendorAttachmentsTypes.gst}
                  onDelete={(data: AttachmentModel): void => {
                    setVendor((prevState) => ({
                      ...prevState,
                      attachments: prevState.attachments.filter(
                        (attachment) => attachment.id !== data.id,
                      ),
                    }));

                    showToast({
                      type: ToastType.success,
                      text: "Attachment removed successfully",
                    });
                  }}
                />
              </div>
            </div>
            <div className="bg-white rounded-md p-2 w-full md:w-1/2 flex flex-col gap-3">
              <div className="grid gap-1">
                <label htmlFor="name" className="text-sm">
                  Vendor Name <RequiredMark />
                </label>
                <input
                  required={true}
                  className="border rounded-md px-3 py-1"
                  type="text"
                  data-type={dataTypes.string}
                  id="name"
                  name="name"
                  value={vendor.name}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="legal_name" className="text-sm">
                  Legal Name{" "}
                  {(vendor.tax_detail.reg_type === "Registered (Composition)" ||
                    vendor.tax_detail.reg_type === "Registered (Regular)") && (
                    <RequiredMark />
                  )}
                </label>
                <input
                  required={
                    vendor.tax_detail.reg_type === "Registered (Composition)" ||
                    vendor.tax_detail.reg_type === "Registered (Regular)"
                  }
                  className="border rounded-md px-3 py-1"
                  type="text"
                  id="legal_name"
                  name="legal_name"
                  value={vendor.legal_name}
                  onChange={handleChange}
                  disabled={isFieldDisabledByGST("legal_name")}
                />
              </div>
              <div className="grid gap-1 ">
                <label htmlFor="type" className="text-sm">
                  Vendor type <RequiredMark />
                </label>
                <select
                  required
                  className="border rounded-md px-3 py-1 capitalize w-[100px]"
                  id="type"
                  name="type"
                  data-type={dataTypes.string}
                  value={vendor.type}
                  onChange={handleChange}
                >
                  <option value=" ">select</option>
                  {PartyType.map((type: any) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-1 ">
                <label htmlFor="segment" className="text-sm">
                  Segment <RequiredMark />
                </label>
                <select
                  required
                  className="border rounded-md px-3 py-1 capitalize w-[100px]"
                  id="segment"
                  name="segment"
                  data-type={dataTypes.string}
                  value={vendor.segment}
                  onChange={handleChange}
                >
                  <option value=" ">select</option>
                  {PartySegment.map((segment: any) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>
          <section className="py-4">
            <h1 className="font-semibold flex gap-2 p-2 bg-white rounded-t-md items-center">
              <span className="text-blue-500">
                <Icon path={mdiEmailFast} size={0.8} />
              </span>
              Contact Details
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 bg-white rounded-b-md p-4">
              <ContactNumberFieldWithCodes
                label="Contact No"
                fieldName="contact_mobile"
                codeFieldName="contact_mobile_country_code"
                vendor={vendor}
                setVendor={setVendor}
                errors={errors}
                setErrors={setErrors}
                required
              />
              <div className="grid gap-1">
                <label htmlFor="mail_detail.contact_name" className="text-sm">
                  Name of Contact Person <RequiredMark />
                </label>
                <input
                  required
                  type="text"
                  data-type={dataTypes.string}
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.contact_name"
                  name="mail_detail.contact_name"
                  value={vendor.mail_detail.contact_name}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="mail_detail.contact_email_contact_person"
                  className="text-sm"
                >
                  Email ID of Contact Person <RequiredMark />
                </label>
                <input
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.contact_email_contact_person"
                  name="mail_detail.contact_email_contact_person"
                  value={vendor.mail_detail.contact_email_contact_person}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="mail_detail.contact_email_ceo_director"
                  className="text-sm"
                >
                  Email ID of CEO/Director
                </label>
                <input
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.contact_email_ceo_director"
                  name="mail_detail.contact_email_ceo_director"
                  value={vendor.mail_detail.contact_email_ceo_director}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="mail_detail.contact_email_account_spoc"
                  className="text-sm"
                >
                  Email ID of Accounts SPOC
                </label>
                <input
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.contact_email_account_spoc"
                  name="mail_detail.contact_email_account_spoc"
                  value={vendor.mail_detail.contact_email_account_spoc}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="mail_detail.contact_email" className="text-sm">
                  Email ID <RequiredMark />
                </label>
                <input
                  required
                  data-type={dataTypes.string}
                  type="email"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.contact_email"
                  name="mail_detail.contact_email"
                  value={vendor.mail_detail.contact_email}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="mail_detail.designation" className="text-sm">
                  Designation
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.designation"
                  name="mail_detail.designation"
                  value={vendor.mail_detail.designation}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="mail_detail.name_ceo_director"
                  className="text-sm"
                >
                  Name of CEO/Director
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.name_ceo_director"
                  name="mail_detail.name_ceo_director"
                  value={vendor.mail_detail.name_ceo_director}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label
                  htmlFor="mail_detail.name_account_spoc"
                  className="text-sm"
                >
                  Name of Accounts SPOC
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.name_account_spoc"
                  name="mail_detail.name_account_spoc"
                  value={vendor.mail_detail.name_account_spoc}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="mail_detail.website" className="text-sm">
                  Website
                </label>
                <input
                  data-type={dataTypes.string}
                  type="text"
                  className="border rounded-md px-3 py-1"
                  id="mail_detail.website"
                  name="mail_detail.website"
                  value={vendor.mail_detail.website}
                  onChange={handleChange}
                />
              </div>
              <ContactNumberFieldWithCodes
                label="Mobile No. of Contact Person"
                fieldName="contact_mobile_contact_person"
                codeFieldName="contact_mobile_contact_person_country_code"
                vendor={vendor}
                setVendor={setVendor}
                errors={errors}
                setErrors={setErrors}
              />
              <ContactNumberFieldWithCodes
                label="Contact Detail of CEO/Director"
                fieldName="contact_detail_ceo_director"
                codeFieldName="contact_detail_ceo_director_country_code"
                vendor={vendor}
                setVendor={setVendor}
                errors={errors}
                setErrors={setErrors}
              />
              <ContactNumberFieldWithCodes
                label="Mobile No. of Accounts SPOC"
                fieldName="contact_mobile_accounts_spoc"
                codeFieldName="contact_mobile_accounts_spoc_country_code"
                vendor={vendor}
                setVendor={setVendor}
                errors={errors}
                setErrors={setErrors}
              />
            </div>
          </section>

          <div className="grid grid-cols-1  md:grid-cols-2 gap-3">
            {/* Address Start */}
            <section className="py-4 bg-white rounded-md p-2 gap-2">
              <h1 className="mb-2 font-semibold flex gap-2 items-center">
                <span className="text-green-500">
                  <Icon path={mdiLocationEnter} size={0.7} />
                </span>
                Address Details
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                <div>
                  <div className="grid gap-1">
                    <label
                      htmlFor="mail_detail.address.line1"
                      className="text-sm"
                    >
                      Registered office address
                      <RequiredMark />
                    </label>
                    <input
                      required
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-1"
                      id="mail_detail.address.line1"
                      name="mail_detail.address.line1"
                      value={vendor.mail_detail.address.line1}
                      onChange={handleChange}
                      placeholder="Line 1"
                      disabled={isFieldDisabledByGST(
                        "mail_detail.address.line1",
                      )}
                    />
                  </div>
                  <div className="grid gap-1">
                    <label
                      htmlFor="mail_detail.address.line2"
                      className="text-sm"
                    >
                      Line2
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-1"
                      id="mail_detail.address.line2"
                      name="mail_detail.address.line2"
                      value={vendor.mail_detail.address.line2}
                      onChange={handleChange}
                      placeholder="Line 2"
                      disabled={isFieldDisabledByGST(
                        "mail_detail.address.line2",
                      )}
                    />
                  </div>
                  <div className="grid gap-1">
                    <label
                      htmlFor="mail_detail.address.line3"
                      className="text-sm"
                    >
                      Line 3
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-1"
                      id="mail_detail.address.line3"
                      name="mail_detail.address.line3"
                      value={vendor.mail_detail.address.line3}
                      onChange={handleChange}
                      placeholder="Line 3"
                      disabled={isFieldDisabledByGST(
                        "mail_detail.address.line3",
                      )}
                    />
                  </div>

                  <div className="flex flex-col items-start text-sm w-full">
                    <div className="flex flex-col items-start text-sm w-full">
                      <label className="font-semibold w-full" htmlFor="country">
                        Country <RequiredMark />
                      </label>
                      <select
                        id="mail_detail.address.country"
                        name="mail_detail.address.country"
                        className="p-1 rounded focus:outline-none w-full border"
                        value={vendor.mail_detail.address.country || ""}
                        onChange={handleCountryChange}
                        disabled={isFieldDisabledByGST(
                          "mail_detail.address.country",
                        )}
                      >
                        <option value="">Select Country</option>
                        {countries.map((c) => (
                          <option key={c.isoCode} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    {/* State */}
                    {states.length > 0 ? (
                      <div className="grid gap-1">
                        <label
                          htmlFor="mail_detail.address.state"
                          className="text-sm"
                        >
                          State{" "}
                          {vendor.mail_detail.address.country === "India" && (
                            <RequiredMark />
                          )}
                        </label>
                        <select
                          id="mail_detail.address.state"
                          name="mail_detail.address.state"
                          className="p-1 rounded border w-full"
                          value={vendor.mail_detail.address.state || ""}
                          onChange={handleStateChange}
                          required={
                            vendor.mail_detail.address.country === "India"
                          }
                          disabled={isFieldDisabledByGST(
                            "mail_detail.address.state",
                          )}
                        >
                          <option value="">Select State</option>
                          {states.map((s) => (
                            <option key={s.isoCode} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="grid gap-1">
                        <label
                          htmlFor="mail_detail.address.state"
                          className="text-sm"
                        >
                          State
                        </label>
                        <input
                          type="text"
                          className="border rounded-md px-3 py-1"
                          id="mail_detail.address.state"
                          name="mail_detail.address.state"
                          value={vendor.mail_detail.address.state}
                          onChange={handleChange}
                          placeholder="Enter state"
                          disabled={isFieldDisabledByGST(
                            "mail_detail.address.state",
                          )}
                        />
                      </div>
                    )}
                    {/* City */}
                    {cities.length > 0 ? (
                      <div className="grid gap-1">
                        <label
                          htmlFor="mail_detail.address.city"
                          className="text-sm"
                        >
                          City <RequiredMark />
                        </label>
                        <select
                          id="mail_detail.address.city"
                          name="mail_detail.address.city"
                          className="p-1 rounded border w-full"
                          value={vendor.mail_detail.address.city || ""}
                          onChange={handleCityChange}
                          disabled={isFieldDisabledByGST(
                            "mail_detail.address.city",
                          )}
                        >
                          <option value="">Select City</option>
                          {cities.map((c) => (
                            <option key={c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="grid gap-1">
                        <label
                          htmlFor="mail_detail.address.city"
                          className="text-sm"
                        >
                          City <RequiredMark />
                        </label>
                        <input
                          type="text"
                          className="border rounded-md px-3 py-1"
                          id="mail_detail.address.city"
                          name="mail_detail.address.city"
                          value={vendor.mail_detail.address.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                          disabled={isFieldDisabledByGST(
                            "mail_detail.address.city",
                          )}
                        />
                      </div>
                    )}
                    <div className="grid gap-1">
                      <label
                        htmlFor="mail_detail.address.pincode"
                        className="text-sm"
                      >
                        Postal code <RequiredMark />
                      </label>
                      <input
                        required={true}
                        type="text"
                        data-type={dataTypes.string}
                        className="border rounded-md px-3 py-1"
                        id="mail_detail.address.pincode"
                        name="mail_detail.address.pincode"
                        value={vendor.mail_detail.address.pincode}
                        onChange={handleChange}
                        disabled={isFieldDisabledByGST(
                          "mail_detail.address.pincode",
                        )}
                      />
                      {errors.pincodeError && (
                        <p className="text-xs text-red-500">
                          {errors.pincodeError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {/* Address End */}

            {/* MSME Start */}
            <section className="py-4 bg-white rounded-md p-2 gap-2">
              <div>
                <h1 className="mb-2 font-semibold flex gap-2 items-center">
                  <span className="text-green-500">
                    <Icon path={mdiLocationEnter} size={0.7} />
                  </span>
                  MSME Registration
                  <div className="flex flex-row gap-6">
                    <div className="flex gap-3 items-center">
                      <input
                        type={dataTypes.radio}
                        data-type={dataTypes.boolean}
                        id="msme_registration_applicable"
                        name="msme_registration_applicable"
                        value={String(
                          vendor.msme_registration_applicable === false,
                        )}
                        checked={vendor.msme_registration_applicable === true}
                        onChange={handleChange}
                        disabled={false}
                      />
                      <label htmlFor="msme_registration">Yes</label>
                    </div>
                    <div className="flex gap-3 items-center">
                      <input
                        type={dataTypes.radio}
                        data-type={dataTypes.boolean}
                        id="msme_registration_applicable"
                        name="msme_registration_applicable"
                        value={String(
                          vendor.msme_registration_applicable === false,
                        )}
                        checked={vendor.msme_registration_applicable === false}
                        onChange={handleChange}
                        disabled={false}
                      />
                      <label htmlFor="msme_registration">No</label>
                    </div>
                  </div>
                </h1>

                <div>
                  {vendor.msme_registration_applicable && (
                    <>
                      <div>
                        <div className="grid gap-1 m-2 text-sm">
                          <label
                            className="w-full"
                            htmlFor="msme_registration_applicable"
                          >
                            MSME Category
                            <RequiredMark />
                          </label>
                          <select
                            value={vendor.msme_registration.msme_category}
                            disabled={false}
                            onChange={handleChange}
                            name="msme_registration.msme_category"
                            id="msme_registration.msme_category"
                            className="p-1 rounded focus:outline-none w-full border"
                          >
                            <option value="default">
                              Select a MSME category
                            </option>
                            {["Micro", "Small", "Medium"].map((type, i) => (
                              <option value={type} key={i}>
                                {type.toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="grid gap-1 m-2">
                          <label
                            htmlFor="msme_registration.msme_registration_number"
                            className="text-sm"
                          >
                            MSME Registration number <RequiredMark />
                          </label>
                          <input
                            required
                            type="text"
                            data-type={dataTypes.string}
                            className="border rounded-md px-3 py-1"
                            id="msme_registration.msme_registration_number"
                            name="msme_registration.msme_registration_number"
                            value={
                              vendor.msme_registration.msme_registration_number
                            }
                            onChange={handleChange}
                          />
                        </div>
                        <div className="grid gap-1 m-2"></div>
                      </div>
                      <div>
                        <label className="text-sm">
                          Please attach MSME certificates{" "}
                          {vendor.msme_registration_applicable && (
                            <RequiredMark />
                          )}
                        </label>
                        <SingleAttachmentSection
                          id="msme_attachment"
                          attachments={(vendor.attachments ?? []).filter(
                            (attachment) =>
                              attachment.category ===
                              VendorAttachmentsTypes.msme,
                          )}
                          category={VendorAttachmentsTypes.msme}
                          onSubmit={function (data: {
                            file: File;
                            category: string;
                            removeSelectedFile: () => void;
                            setShowUpload: (v: boolean) => void;
                          }): void {
                            setLoading(true);
                            uploadFile({
                              file: data.file,
                              category: VendorAttachmentsTypes.msme,
                            });
                            setLoading(false);
                          }}
                          onDelete={(data: AttachmentModel): void => {
                            setVendor((prevState) => ({
                              ...prevState,
                              attachments: prevState.attachments.filter(
                                (attachment) => attachment.id !== data.id,
                              ),
                            }));

                            showToast({
                              type: ToastType.success,
                              text: "Attachment removed successfully",
                            });
                          }}
                          onSelect={function (data: AttachmentModel): void {
                            throw new Error("Function not implemented.");
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>
            {/* MSME end */}
          </div>

          <div className="grid grid-cols-1  md:grid-cols-2 gap-3  ">
            <section className="py-4">
              <h1 className="p-2 font-semibold flex gap-2 items-center bg-white rounded-t-md">
                <span className="text-green-500">
                  <Icon path={mdiBank} size={0.7} />
                </span>
                Bank Details
              </h1>
              <div className="grid grid-cols-1  md:grid-cols-2 gap-3 bg-white rounded-b-md p-2">
                <div className="grid gap-0.5">
                  <label
                    htmlFor="bank_detail.account_holder_name"
                    className="text-sm"
                  >
                    Account holder name <RequiredMark />
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1"
                    id="bank_detail.account_holder_name"
                    name="bank_detail.account_holder_name"
                    value={vendor.bank_detail.account_holder_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="grid gap-0.5">
                  <label htmlFor="bank_detail.ac_no" className="text-sm">
                    Account no.
                    <RequiredMark />
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1"
                    id="bank_detail.ac_no"
                    name="bank_detail.ac_no"
                    value={vendor.bank_detail.ac_no}
                    onChange={handleChange}
                  />
                </div>
                <div className="grid gap-0.5">
                  <label htmlFor="bank_detail.bank_name" className="text-sm">
                    Bank Name
                    <RequiredMark />
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1"
                    id="bank_detail.bank_name"
                    name="bank_detail.bank_name"
                    value={vendor.bank_detail.bank_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="grid gap-0.5">
                  <label htmlFor="bank_detail.bank_address" className="text-sm">
                    Bank Address
                  </label>
                  <textarea
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1"
                    id="bank_detail.bank_address"
                    name="bank_detail.bank_address"
                    value={vendor.bank_detail.bank_address}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  {vendor.mail_detail.address.country === "India" ? (
                    <div className="grid gap-0.5">
                      <label htmlFor="bank_detail.ifsc" className="text-sm">
                        IFSC <RequiredMark />
                      </label>
                      <input
                        type="text"
                        data-type={dataTypes.string}
                        className="border rounded-md px-3 py-1"
                        id="bank_detail.ifsc"
                        name="bank_detail.ifsc"
                        value={vendor.bank_detail.ifsc}
                        onChange={handleChange}
                      />
                    </div>
                  ) : (
                    <div className="grid gap-0.5">
                      <label
                        htmlFor="bank_detail.swift_code"
                        className="text-sm"
                      >
                        Swift code
                      </label>
                      <input
                        type="text"
                        data-type={dataTypes.string}
                        className="border rounded-md px-3 py-1"
                        id="bank_detail.swift_code"
                        name="bank_detail.swift_code"
                        value={vendor.bank_detail.swift_code}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
                <div className="grid gap-0.5">
                  <label htmlFor="bank_detail.bank_name" className="text-sm">
                    MICR Code
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1"
                    id="bank_detail.micr_code"
                    name="bank_detail.micr_code"
                    value={vendor.bank_detail.micr_code}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>
            <section className="py-4 bg-white rounded-md my-4 p-2">
              <h1 className="p-2 font-semibold flex gap-2 items-center bg-white rounded-t-md">
                <span className="text-green-500">
                  <Icon path={mdiBank} size={0.7} />
                </span>
                Details
              </h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="grid gap-0.5">
                  <label htmlFor="tax_detail.pan" className="text-sm">
                    PAN <RequiredMark />
                  </label>
                  <input
                    required
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1 w-full"
                    id="tax_detail.pan"
                    name="tax_detail.pan"
                    value={vendor.tax_detail.pan}
                    onChange={handleChange}
                    disabled={isFieldDisabledByGST("tax_detail.pan")}
                  />
                  {errors.panError && (
                    <p className="text-xs text-red-500">{errors.panError}</p>
                  )}
                </div>

                <div className="grid gap-0.5">
                  <label htmlFor="tax_detail.gstType" className="text-sm">
                    Choose status - Operative/ Inoperative{" "}
                    {vendor.mail_detail.address.country === "India" && (
                      <RequiredMark />
                    )}
                  </label>
                  <select
                    id="tax_detail.gstType"
                    name="tax_detail.gstType"
                    className="border rounded-md px-3 py-1"
                    data-type={dataTypes.string}
                    value={vendor.tax_detail.gstType}
                    onChange={handleChange}
                    disabled={!canEdit}
                  >
                    <option value="">Select</option>
                    {Object.values(gstTypes).map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-0.5">
                  <label htmlFor="type" className="text-sm">
                    Legal Entity <RequiredMark />
                  </label>
                  <select
                    required
                    className="border rounded-md px-3 py-1 capitalize w-full truncate text-ellipsis"
                    id="legal_entity_type"
                    name="legal_entity_type"
                    data-type={dataTypes.string}
                    value={vendor.legal_entity_type}
                    onChange={(e) => {
                      setVendor((o) => ({
                        ...o,
                        legal_entity_type: e.target.value, // Update state with selected value
                      }));
                    }}
                    // onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {LegalEntityType.map((type: any) => (
                      <option key={type} value={type} className="truncate">
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {vendor.legal_entity_type === "Sole Proprietorship" && (
                  <div className="grid gap-0.5">
                    <label
                      htmlFor="tax_detail.adhaar_number"
                      className="text-sm"
                    >
                      ADHAAR Number
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-1 w-full"
                      id="adhaar_number"
                      name="adhaar_number"
                      value={vendor.adhaar_number}
                      onChange={handleChange}
                    />
                  </div>
                )}

                <div className="grid gap-0.5">
                  <label
                    htmlFor="tax_detail.cin_llpin_number"
                    className="text-sm"
                  >
                    CIN/LLPIN Number
                  </label>
                  <input
                    type="text"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1 w-full"
                    id="cin_llpin_number"
                    name="cin_llpin_number"
                    value={vendor.cin_llpin_number}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid gap-0.5">
                  <label htmlFor="date_of_commencement" className="text-sm">
                    Date of Commencement of Business
                  </label>
                  <input
                    type="date"
                    data-type={dataTypes.string}
                    className="border rounded-md px-3 py-1 w-full"
                    id="date_of_commencement"
                    name="date_of_commencement"
                    value={
                      vendor.date_of_commencement
                        ? moment(vendor.date_of_commencement).format(
                            "YYYY-MM-DD",
                          )
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <section className="py-4 bg-white rounded-md p-2">
              <label className="text-sm">Please attach KYC documents</label>
              <MultiAttachmentSection
                id="kyc_attachments"
                attachments={(vendor.attachments ?? []).filter(
                  (attachment) =>
                    attachment.category === VendorAttachmentsTypes.common,
                )}
                onSubmit={async function (data: {
                  file: File;
                  category: string;
                }) {
                  const attachment = await uploadFile({
                    file: data.file,
                    category: data.category,
                  });
                  if (!attachment) {
                    throw new Error("File upload failed");
                  }
                  return attachment;
                }}
                category={VendorAttachmentsTypes.common}
                onDelete={function (data: AttachmentModel): void {
                  setVendor((prevState) => ({
                    ...prevState,
                    attachments: prevState.attachments.filter(
                      (attachment) => attachment.id !== data.id,
                    ),
                  }));

                  showToast({
                    type: ToastType.success,
                    text: "Attachment removed successfully",
                  });
                }}
              />
            </section>
          </div>

          <div className="flex flex-row flex-wrap justify-end gap-2 m-2">
            <div className="flex gap-2">
              {isMaker &&
                (vendor.status !== PartyStatusMap.Approved &&
                vendor.status !== PartyStatusMap["Approve Pending"] &&
                vendor.status !== PartyStatusMap["Update Pending"] ? (
                  <>
                    <button
                      id="save_draft_button"
                      onClick={handleSaveDraft}
                      form="party_create_form"
                      type="button"
                      className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm cursor-pointer duration-300"
                    >
                      Save Draft
                    </button>
                    <button
                      id="submit_button"
                      form="party_create_form"
                      type="submit"
                      className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm cursor-pointer duration-300"
                    >
                      Submit
                    </button>
                  </>
                ) : null)}
              <button
                id="quit_button"
                onClick={() => {
                  navigation.goBack();
                }}
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
              >
                Quit
              </button>
            </div>
          </div>
        </fieldset>
      </form>
      {creating && <LoadingWidget />}
      {loading && <LoadingWidget />}
    </div>
  );
};

const RequiredMark = () => (
  <>
    <span className="text-red-500">*</span>
  </>
);

export { isGstUniqueInSystem };
export default VendorCreateScreen;
